import { AxiosResponse } from 'axios';
import { createKeys } from './createKeys';
import {
  createModel,
  ParamsWithId,
  ParamsWithIds,
  BasicParams,
  BasicParamsWithContentRange,
} from './createModel';
import { useCreateQuery, QueryOptions } from './useCreateQuery';
import {
  useCreateInfiniteQuery,
  InfiniteQueryOptions,
} from './useCreateInfiniteQuery';
import {
  useCreateContentRangeQuery,
  ContentRangeQueryOptions,
} from './useCreateContentRangeQuery';
import { useCreateMutation, MutationOptions } from './useCreateMutation';

interface TMutationFactoryProps<T> {
  fetchFunction: (params: object) => Promise<T>;
  isRestV2?: boolean;
}

export const createCrud = <ModelType>(
  entityName: string,
  fetchKey: string = '',
) => {
  const keys = createKeys(entityName, fetchKey, false);
  const keysV2 = createKeys(entityName, fetchKey, true);
  const model = createModel<ModelType>(keys.basicUrlPath, false);
  const modelV2 = createModel<ModelType>(keysV2.basicUrlPath, true);

  const mutationFactory =
    ({ fetchFunction, isRestV2 }: TMutationFactoryProps<ModelType>) =>
    () =>
    (options?: MutationOptions<ModelType>) =>
      useCreateMutation<ModelType>(
        isRestV2 ? keysV2.all : keys.all,
        fetchFunction,
        keys.fetchKey,
        options,
      );

  return {
    keys,
    fetchById:
      () => (params: ParamsWithId, options?: QueryOptions<ModelType>) =>
        useCreateQuery<ModelType>(
          keys.byId(params.id),
          model.fetchById,
          params,
          options,
        ),
    fetchByIds:
      () => (params: ParamsWithIds, options?: QueryOptions<ModelType[]>) =>
        useCreateQuery<ModelType[]>(
          keys.byIds(params),
          model.fetchByIds,
          params,
          options,
        ),
    fetchMany:
      () =>
      (params?: BasicParams, options?: InfiniteQueryOptions<ModelType[]>) =>
        useCreateInfiniteQuery<ModelType[]>(
          keys.many(params),
          model.fetchMany,
          params,
          options,
        ),
    count: () => (params?: BasicParams, options?: QueryOptions<number>) =>
      useCreateQuery<number>(
        [...keys.many(params), 'count'],
        model.count,
        params,
        options,
      ),
    fetchPaginated:
      () =>
      (params?: BasicParams, options?: InfiniteQueryOptions<ModelType[]>) =>
        useCreateInfiniteQuery<ModelType[]>(
          keys.many(params),
          model.fetchMany,
          params,
          options,
        ),
    create: mutationFactory({ fetchFunction: model.create }),
    edit: mutationFactory({ fetchFunction: model.edit }),
    delete: mutationFactory({ fetchFunction: model.delete }),
    deleteByIds: mutationFactory({ fetchFunction: model.deleteByIds }),

    // methods using RestV2 endpoints
    keysV2,
    fetchByIdV2:
      () => (params: ParamsWithId, options?: QueryOptions<ModelType>) =>
        useCreateQuery<ModelType>(
          keysV2.byId(params.id),
          modelV2.fetchById,
          params,
          options,
        ),
    fetchByIdsV2:
      () => (params: ParamsWithIds, options?: QueryOptions<ModelType[]>) =>
        useCreateQuery<ModelType[]>(
          keysV2.byIds(params),
          modelV2.fetchByIds,
          params,
          options,
        ),
    fetchManyV2:
      () =>
      (params?: BasicParams, options?: InfiniteQueryOptions<ModelType[]>) =>
        useCreateInfiniteQuery<ModelType[]>(
          keysV2.many(params),
          modelV2.fetchMany,
          params,
          options,
        ),
    countV2: () => (params?: BasicParams, options?: QueryOptions<number>) =>
      useCreateQuery<number>(
        [...keys.many(params), 'count'],
        modelV2.count,
        params,
        options,
      ),
    fetchPaginatedV2:
      () =>
      (params?: BasicParams, options?: InfiniteQueryOptions<ModelType[]>) =>
        useCreateInfiniteQuery<ModelType[]>(
          keysV2.many(params),
          modelV2.fetchMany,
          params,
          options,
        ),
    fetchManyByContentRangeV2:
      () =>
      (
        params: BasicParamsWithContentRange,
        options?: ContentRangeQueryOptions<{
          data: ModelType[];
          headers: AxiosResponse['headers'];
        }>,
      ) =>
        useCreateContentRangeQuery<{
          data: ModelType[];
          headers: AxiosResponse['headers'];
        }>(
          keysV2.manyByContentRange(params),
          modelV2.fetchManyByContentRange,
          params,
          options,
        ),
    createV2: mutationFactory({
      fetchFunction: modelV2.create,
      isRestV2: true,
    }),
    editV2: mutationFactory({ fetchFunction: modelV2.edit, isRestV2: true }),
    deleteV2: mutationFactory({
      fetchFunction: modelV2.delete,
      isRestV2: true,
    }),
    deleteByIdsV2: mutationFactory({
      fetchFunction: modelV2.deleteByIds,
      isRestV2: true,
    }),
  };
};
